import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Typography,
  SelectChangeEvent,
  Alert,
  CircularProgress,
  Skeleton,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Collapse
} from '@mui/material';
import Grid from '@mui/material/Grid';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { 
  TrendingUp, 
  TrendingDown, 
  TrendingFlat,
  SignalCellularAlt,
  Battery80,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CloudOff as CloudOffIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { LocationStatus, poursAPI, ChartDataResponse, ChartTarget, Pour2Limit } from '../../services/pours';
import type { CalibrationCheck, MaturityDescriptor } from '../../services/pours';
import { formatDateTimeWithZone } from '../../utils/datetime';
import { apiErrorText } from '../../utils/apiError';
import { areaFill, getBaseTimeSeriesOptions } from './chartOptions';
import { TargetsCard } from './TargetsCard';
import {
  REACHED_MARKER_COLOR,
  TREND_ESTIMATE_TOOLTIP,
  achievementPoints,
  declutterTargetLabels,
  extrapolatedZones,
  maturityAxisSoftMax,
  outsideTestedSpan,
  strengthAxisSoftMax,
  strengthRangeCaption,
  targetLabelPriority,
  targetPlotLineId,
  testedRangeBand,
  testedTimeSpan,
} from './strengthChart';
import type { AchievementPoint } from './strengthChart';
import {
  getOnlineStatus,
  isLoggingEnded,
  locationStartsAt,
  pourStatusEnded,
} from '../../utils/onlineStatus';
import { temperatureLimitLines } from './limitLines';
import { FRESHNESS_TINTS, formatBatteryLevel } from './locationDisplay';
import { EventTimeline } from './EventTimeline';
import { locationHasMaturity } from '../../utils/locationMaturity';
import {
  READING_INTERVAL_WARNING,
  TARGET_OUTSIDE_RANGE_LABEL,
  formatHalfUp,
  formatMaturity,
  formatMaturityWithIndex,
  formatStrengthReading,
  isTargetOutsideRange,
  maturityMeta,
  resolveStrengthReading,
  resolveStrengthUnit,
  strengthReadingTooltip,
  targetDisplayName,
  targetPurpose,
} from '../../utils/maturityDisplay';

// Load Highcharts modules
// eslint-disable-next-line
require('highcharts/modules/exporting');

interface LocationDetailsProps {
  locations: LocationStatus[];
  pourId: string;
  initialLocationId?: string;
  pourFunctionality?: string;
  /** Comma-joined granular monitoring functions (e.g. "Temperature Only,
   *  Concrete Maturity"). When present, drives exactly which graphs show;
   *  falls back to pourFunctionality for legacy pours that lack it. */
  monitoringFunctions?: string;
  /** The pour's stored maturity method. Only "Nurse Saul" is TTF (°C·hrs);
   *  every other value is EA (hrs). Used when no descriptor is available. */
  maturityMethod?: string;
  /** The pour's maturity naming from the dashboard (PourSummary.maturity).
   *  The chart response's own descriptor wins once loaded. */
  maturity?: MaturityDescriptor | null;
  /** IANA timezone of the pour (e.g. "Australia/Sydney") so timestamps render
   *  in the project's local time instead of the browser's. */
  timezoneIana?: string;
  /** The pour's configured limits (from getPourLimits) — the same source the
   *  Temperature Profile bars use. The temperature chart draws its limit
   *  plotlines from the Criteria '02' rows here so the two always agree. */
  pourLimits?: Pour2Limit[];
  /** The company strength unit (MPa / Psi / N/mm²) for the strength KPI,
   *  chart axis and tooltip. The chart response's unit wins once loaded. */
  strengthUnit?: string;
  /** Called after the concrete start is reset, so the page reloads the
   *  recalculated maturity, strength and targets. */
  onMaturityChanged?: () => void;
  /** The user may reset the concrete start by double-clicking a temperature
   *  reading: they can edit pours (pours.create, the permission the API
   *  checks) and the pour is not locked. When false the chart neither reacts
   *  to the double-click nor invites it in the tooltip. */
  canResetConcreteStart?: boolean;
  /** Bumped by the page on each refresh (its 5-minute poll, the refresh
   *  button): the selected location's readings reload quietly, without the
   *  loading skeletons, so the selection and the charts' zoom stay. */
  refreshSignal?: number;
  /** The pour's status code ('2' = completed): a completed pour's locations
   *  show "Logging ended" instead of "Offline". */
  pourStatus?: string;
  /** The pour against its calibration (ASTM C1074 9.2): a 'mismatch' pour
   *  shows no strength, and the Targets card says why. */
  calibrationCheckStatus?: CalibrationCheck['status'] | null;
}

// No configured limits: one shared empty list, so the memoised temperature
// chart options are not rebuilt on every render.
const NO_LIMITS: Pour2Limit[] = [];

// Stacked-graph height — shorter than the old single-tab 500 so several
// graphs read comfortably down the page without excessive scrolling.
const STACK_CHART_HEIGHT = 420;

// Series colours of the maturity and strength charts. The strength colour
// is also the base of its faded trend-estimate zones.
const MATURITY_COLOR = '#9c27b0';
const STRENGTH_COLOR = '#1976d2';

/** The "Targets reached" markers on a maturity or strength curve: one point
 *  per achievement time, its details on hover (strengthChart's
 *  achievementPoints). No series without points. The points carry no name:
 *  Highcharts heads the tooltip with a point's name when it has one, so
 *  without it the header is the time the targets were reached, as on the
 *  curve. */
const reachedMarkerSeries = (points: AchievementPoint[]): Highcharts.SeriesScatterOptions[] =>
  points.length === 0 ? [] : [{
    type: 'scatter',
    name: 'Targets reached',
    data: points.map(({ x, y, marker, custom }) => ({ x, y, marker, custom })),
    color: REACHED_MARKER_COLOR,
    marker: { enabled: true, radius: 5, symbol: 'circle', lineColor: '#ffffff', lineWidth: 1.5 },
    dataGrouping: { enabled: false },
    showInLegend: false,
    zIndex: 6,
    tooltip: {
      headerFormat: '<span style="font-size: 0.8em">{point.key}</span><br/>',
      pointFormatter: function (this: Highcharts.Point) {
        const lines = (this.options.custom as { lines?: string[] } | undefined)?.lines ?? [];
        return lines.join('<br/>');
      },
    },
  }];

/** One metric in the horizontal KPI strip above the charts: a label, a value
 *  (optionally tooltip-wrapped, e.g. the below-range strength note) and an
 *  optional sub-line such as the TTF / max-temp time. */
const KpiCell: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  color?: string;
  tooltip?: string;
}> = ({ label, value, sub, color, tooltip }) => {
  const valueNode = (
    <Typography
      variant="h6"
      fontSize="1.05rem"
      fontWeight={600}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color, lineHeight: 1.3 }}
    >
      {value}
    </Typography>
  );
  return (
    <Box sx={{ minWidth: 0, bgcolor: 'grey.50', borderRadius: 1.5, p: 1.25 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" noWrap>
        {label}
      </Typography>
      {tooltip ? (
        <Tooltip title={tooltip} placement="top" arrow>
          {valueNode}
        </Tooltip>
      ) : (
        valueNode
      )}
      {sub && (
        <Typography variant="caption" color="text.disabled" display="block" fontSize="0.72rem">
          {sub}
        </Typography>
      )}
    </Box>
  );
};

export const LocationDetails: React.FC<LocationDetailsProps> = ({
  locations,
  pourId,
  initialLocationId,
  pourFunctionality = '1',
  monitoringFunctions,
  maturityMethod,
  maturity,
  timezoneIana,
  pourLimits = NO_LIMITS,
  strengthUnit,
  onMaturityChanged,
  canResetConcreteStart = false,
  refreshSignal,
  pourStatus,
  calibrationCheckStatus,
}) => {
  // Decide which graphs this pour needs. Prefer the granular monitoring
  // functions (so e.g. a Temperature-Only pour shows just the temperature
  // graph); fall back to the legacy '1'/'2'/'3' code when they're absent.
  //   '1' Maturity only            → EA/Strength, no Temp Diff
  //   '2' Mass Concrete only       → Temp Diff, no EA/Strength
  //   '3' Maturity & Mass Concrete → both
  const fns = (monitoringFunctions ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const hasFns = fns.length > 0;
  // Temperature is the prerequisite of every function, so it always shows.
  const showEvaporationTab = hasFns
    ? fns.includes('Calculate Concrete Evaporation Rate')
    : false; // legacy: evaporation only meaningful for mass-concrete weather fns
  const showMaturityTab = hasFns
    ? fns.includes('Concrete Maturity')
    : pourFunctionality === '1' || pourFunctionality === '3';
  // Ambient monitoring pour: only non-concrete functions selected (e.g.
  // External Temperature and Humidity / Onsite Weather). Concrete-specific
  // wording (pour, concrete start) is swapped for generic monitoring labels.
  const CONCRETE_FNS = [
    'Concrete Maturity',
    'Temperature Only',
    'Temperature Match Curing',
    'Calculate Concrete Evaporation Rate',
  ];
  const isAmbientPour = hasFns && !fns.some((f) => CONCRETE_FNS.includes(f));
  // Differential is no longer gated on the pour's monitoring function — a
  // Temperature Only pour can still have differential limits configured between
  // locations. The chart is gated purely on whether differential data exists
  // for the selected location (see the `difftemp` check below).
  const [selectedLocationId, setSelectedLocationId] = useState<string>(
    initialLocationId || (locations.length > 0 ? locations[0].id : '')
  );

  // Sync when the parent updates the initial selection (e.g. user clicks a
  // different row on the Monitoring Locations tab).
  useEffect(() => {
    if (initialLocationId && initialLocationId !== selectedLocationId) {
      setSelectedLocationId(initialLocationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLocationId]);

  const [railOpen, setRailOpen] = useState(true);
  const [chartData, setChartData] = useState<ChartDataResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The ASTM C1074 7.3 warning's list of gaps, collapsed again for each
  // location.
  const [gapsOpen, setGapsOpen] = useState(false);
  useEffect(() => {
    setGapsOpen(false);
  }, [selectedLocationId]);

  // Maturity naming: the chart response's descriptor, else the dashboard's,
  // else the backend rule on the stored method (only "Nurse Saul" is TTF in
  // °C·hrs; everything else is Equivalent Age at the specified temperature,
  // in hrs). Strength carries the company unit.
  const meta = useMemo(
    () => maturityMeta(chartData?.maturity ?? maturity ?? maturityMethod),
    [chartData?.maturity, maturity, maturityMethod],
  );
  const maturityLong = meta.index_label;
  const maturityUnit = meta.unit;
  const unit = resolveStrengthUnit(chartData?.strength_unit, strengthUnit);

  // EA Reset state
  const [eaResetDialogOpen, setEaResetDialogOpen] = useState(false);
  const [selectedEAStartTime, setSelectedEAStartTime] = useState<number | null>(null);
  const [eaResetLoading, setEaResetLoading] = useState(false);
  const [eaResetError, setEaResetError] = useState<string | null>(null);

  // Double-click detection: the last click on a temperature reading. Refs,
  // not state, so the memoised chart options always see the latest click.
  const lastClickTime = useRef(0);
  const lastClickX = useRef<number | null>(null);

  const selectedLocation = locations.find(loc => loc.id === selectedLocationId);

  // Humidity channels (ConXenviro SHT40) report %RH through the same value
  // fields — labels/units below switch accordingly.
  const isHumidity =
    (selectedLocation?.channel_type || '').toUpperCase() === 'HUMIDITY';
  const valueUnit = isHumidity ? '%' : '°C';

  // Site-conditions hardware ('07' weather station, '09' Digit Sensor,
  // '10' ConXenviro): these channels measure the environment, not the
  // concrete, so the pour's temperature limits and differentials never
  // apply to them — the charts skip both. So does a concrete-hardware
  // channel marked ambient (is_ambient).
  const isSiteHardware =
    selectedLocation?.logger_type === '07' ||
    selectedLocation?.logger_type === '09' ||
    selectedLocation?.logger_type === '10' ||
    !!selectedLocation?.is_ambient;

  // The selected location shows generic monitoring events (no concrete
  // milestones) when it is site hardware — on ANY pour, not just pure
  // ambient pours.
  const isAmbientView = isAmbientPour || isSiteHardware;

  // Whether the selected location accrues maturity. Humidity channels, site
  // hardware and Temperature Only locations (MonitorType '2', even on a
  // maturity pour) have no maturity, strength, progress or targets.
  const locationMaturity = selectedLocation ? locationHasMaturity(selectedLocation) : true;

  const handleLocationChange = (event: SelectChangeEvent) => {
    setSelectedLocationId(event.target.value);
  };

  // Get temperature trend icon
  const getTempTrendIcon = (trend?: string) => {
    if (!trend) return null;
    if (trend === 'up') return <TrendingUp fontSize="small" color="error" />;
    if (trend === 'down') return <TrendingDown fontSize="small" color="primary" />;
    return <TrendingFlat fontSize="small" />;
  };

  // Load a location's chart data. Each load is numbered so an older, slower
  // response (another location, or a refresh a newer load overtook) never
  // replaces a newer one. A quiet load (the page's refresh) keeps what is on
  // screen: no skeletons, so the charts stay mounted with their zoom, and a
  // failure leaves the last data in place, but only when that data is this
  // location's. A quiet load can overtake the skeleton load of a newly
  // picked location; if it then fails, the previous location's data must
  // not stay on screen under the new one, so it fails like a first load.
  const loadSeq = useRef(0);
  const chartLocationRef = useRef<string | null>(null);
  const loadChart = useCallback(async (locationId: string, quiet: boolean) => {
    if (!locationId || !pourId) return;
    const seq = ++loadSeq.current;
    if (!quiet) {
      setLoading(true);
      setError(null);
    }
    try {
      const data = await poursAPI.getLocationChartData(pourId, locationId);
      if (seq !== loadSeq.current) return;
      chartLocationRef.current = locationId;
      setChartData(data);
      setError(null);
    } catch (err: any) {
      if (seq !== loadSeq.current) return;
      console.error('Error fetching chart data:', err);
      const ownData = chartLocationRef.current === locationId;
      if (!ownData) {
        chartLocationRef.current = null;
        setChartData(null);
      }
      if (!quiet || !ownData) setError(apiErrorText(err, 'Failed to load chart data'));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [pourId]);

  // A newly selected location loads with skeletons.
  useEffect(() => {
    loadChart(selectedLocationId, false);
  }, [selectedLocationId, loadChart]);

  // The page's refresh reloads the selected location quietly. Only a change
  // of the signal triggers it (not the first render).
  const lastRefreshSignal = useRef(refreshSignal);
  useEffect(() => {
    if (refreshSignal === lastRefreshSignal.current) return;
    lastRefreshSignal.current = refreshSignal;
    loadChart(selectedLocationId, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  // Handle EA start reset
  const handleEAPointDoubleClick = (timestamp: number) => {
    if (!canResetConcreteStart) return;
    setSelectedEAStartTime(timestamp);
    setEaResetError(null);
    setEaResetDialogOpen(true);
  };

  const handleEAResetConfirm = async () => {
    if (!selectedEAStartTime || !selectedLocationId) return;

    setEaResetLoading(true);
    setEaResetError(null);
    try {
      await poursAPI.resetEAStart(selectedLocationId, selectedEAStartTime);
    } catch (err: any) {
      // Shown in the dialog, which stays open.
      console.error('Error resetting concrete start:', err);
      setEaResetError(apiErrorText(err, 'Failed to change the concrete start'));
      setEaResetLoading(false);
      return;
    }
    setEaResetDialogOpen(false);
    setSelectedEAStartTime(null);
    // The page's summary (maturity, strength, targets) and this location's
    // charts both show the recalculated values.
    onMaturityChanged?.();
    setError(null);
    // Supersede a refresh still in flight: this reload has the recalculated
    // values. (No skeleton load can be running: the chart was on screen.)
    ++loadSeq.current;
    try {
      const data = await poursAPI.getLocationChartData(pourId, selectedLocationId);
      chartLocationRef.current = selectedLocationId;
      setChartData(data);
    } catch (err: any) {
      console.error('Error fetching chart data:', err);
      setError(apiErrorText(err, 'Failed to load chart data'));
    } finally {
      setEaResetLoading(false);
    }
  };

  const handleEAResetCancel = () => {
    if (eaResetLoading) return;
    setEaResetDialogOpen(false);
    setSelectedEAStartTime(null);
    setEaResetError(null);
  };

  // Temperature Chart Configuration
  const getTempChartOptions = (): Highcharts.Options => {
    const tempSeries: Highcharts.SeriesOptionsType[] = [];
    
    // Add logger temperature series — name matches legacy ConXedge: the
    // location's display name (e.g. "Concrete"), so the tooltip header reads
    // "Concrete: 25.0°C" instead of a generic label.
    const locationLabel = chartData?.location_name || 'Logger Temperature';

    if (chartData?.temp && chartData.temp.length > 0) {
      tempSeries.push({
        name: locationLabel,
        type: 'areaspline',
        data: chartData.temp,
        threshold: null,
        fillColor: areaFill('#79A82A'),
        marker: { enabled: false, radius: 2 },
        color: '#79A82A',
        // Double-click a reading to reset the concrete start: editors only,
        // never on a locked pour (the API refuses both).
        ...(canResetConcreteStart ? {
          cursor: 'pointer' as const,
          point: {
            events: {
              click: function(this: any) {
                const clickTime = new Date().getTime();
                const clickX = this.x;

                // Check if this is a double-click (within 300ms of last click on same point)
                if (clickTime - lastClickTime.current < 300 && clickX === lastClickX.current) {
                  handleEAPointDoubleClick(clickX);
                  lastClickTime.current = 0;
                  lastClickX.current = null;
                } else {
                  lastClickTime.current = clickTime;
                  lastClickX.current = clickX;
                }
              }
            }
          },
        } : {}),
      });
    }
    
    // Add tank reference series — legacy named this `${location_name} Tank`
    // (e.g. "Concrete Tank"). Sourced from TankTempTrack via the location's
    // TankID, so it only appears when the location has tank data.
    if (chartData?.tankTemp && chartData.tankTemp.length > 0) {
      tempSeries.push({
        name: `${locationLabel} Tank`,
        type: 'spline',
        data: chartData.tankTemp,
        marker: { enabled: false, radius: 2 },
        color: '#ff9800'
      });
    }

    // Add temperature limit plotlines. Prefer the pour's configured limits
    // (Criteria '02') — the same source the Temperature Profile bars read —
    // so the dashed lines always match the profile: real operator from
    // LimitsType, value from LimitsTemp, and the configured colour/Purpose.
    // Fall back to the per-location chart limits only when none were passed.
    // Keep every limit line on-screen regardless of how cool the location
    // runs: softMax floors the axis maximum at the highest limit (+a little
    // headroom for its label) while still letting hotter data push higher.
    const { plotLines, softMax: limitSoftMax } = temperatureLimitLines(
      pourLimits,
      chartData?.limit,
      { isSiteHardware, labels: 'full' },
    );

    return Highcharts.merge<Highcharts.Options>(getBaseTimeSeriesOptions(
      isHumidity ? 'Relative Humidity vs Elapsed Time' : 'Temperature vs Elapsed Time',
      { timezoneIana, height: STACK_CHART_HEIGHT },
    ), {
      yAxis: {
        title: { text: isHumidity ? 'Relative Humidity (%)' : 'Temperature (°C)' },
        opposite: false,
        min: 0,
        // RH is bounded 0-100; temperature keeps the limit-driven soft max.
        ...(isHumidity ? { max: 100 } : { softMax: limitSoftMax }),
        plotLines: isHumidity ? [] : plotLines
      },
      legend: { enabled: tempSeries.length > 1 },
      tooltip: {
        // Match legacy: only the hovered series shows in the tooltip, not
        // both temp + tank values stacked.
        shared: false,
        split: false,
        valueDecimals: 1,
        valueSuffix: isHumidity ? ' %' : '°C',
        headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
        // The double-click hint shows only to users who may reset the
        // concrete start (see canResetConcreteStart).
        pointFormat: isHumidity
          ? '<span style="color:{series.color}">{series.name}</span>: <b>{point.y} %</b><br/>'
          : '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}°C</b><br/>'
            + (canResetConcreteStart
              ? '<span class="tooltip"><i>Double click - Change concrete start to this reading</i></span>'
              : '')
      },
      series: tempSeries.length > 0 ? tempSeries : [{
        name: 'No data',
        type: 'spline',
        data: []
      }]
    });
  };

  // Evaporation Chart Configuration
  const getEvaChartOptions = (): Highcharts.Options => {
    const evaSeries: Highcharts.SeriesOptionsType[] = [];

    if (chartData?.eva && chartData.eva.length > 0) {
      evaSeries.push({
        name: 'Evaporation Rate',
        type: 'areaspline',
        data: chartData.eva,
        threshold: null,
        fillColor: areaFill('#4caf50'),
        marker: { enabled: false, radius: 2 },
        color: '#4caf50'
      });
    }
    
    // Add evaporation limit plotlines
    const plotLines: Highcharts.YAxisPlotLinesOptions[] = [];
    if (chartData?.evaLimit) {
      chartData.evaLimit.forEach((limit) => {
        plotLines.push({
          value: limit.value,
          color: 'red',
          width: 2,
          dashStyle: 'Dash',
          label: {
            text: `Limit ${limit.operator} ${limit.value}`,
            align: 'right',
            x: -8,
            style: { color: 'red', fontSize: '11px' }
          },
          zIndex: 5
        });
      });
    }
    
    return Highcharts.merge<Highcharts.Options>(getBaseTimeSeriesOptions('Evaporation Rate vs Elapsed Time', { timezoneIana, height: STACK_CHART_HEIGHT }), {
      yAxis: {
        title: { text: 'Evaporation Rate (kg/m²/h)' },
        opposite: false,
        min: 0,
        plotLines
      },
      tooltip: { valueDecimals: 2, valueSuffix: ' kg/m²/h' },
      series: evaSeries.length > 0 ? evaSeries : [{
        name: 'No data',
        type: 'spline',
        data: []
      }]
    });
  };

  // ea rows are [timestamp, strength|null, ttf]; strength is null without a
  // usable curve and outside the calibration's plot window. Used to decide
  // whether the per-location strength chart has anything to draw.
  const hasStrengthData = (chartData?.ea ?? []).some((p) => p[1] != null);
  // The calibration's tested range and whether its curve is usable (newer
  // responses); undefined on older responses.
  const calibratedRange = chartData?.calibrated_range ?? null;
  const hasCurve = calibratedRange ? calibratedRange.has_curve : undefined;
  // Targets whose strength or maturity lies outside the tested range
  // (flagged, allowed).
  const targetOutside = (t: ChartTarget) => isTargetOutsideRange(t, calibratedRange);
  // Target times are measured from the concrete start (maturity counted
  // from), as legacy did; the monitoring start stands in without one.
  const concreteStart = chartData?.location_details?.ea_started ?? chartData?.ea_start;
  const monitoringStart = chartData?.location_details?.logging_started;

  // The maturity and strength curves ([time, value]) from the ea rows
  // ([timestamp, strength|null, maturity]; older backends sent the maturity
  // in the second slot), a marker on each where a target was reached, and
  // when this location's maturity was inside the tested range (the strength
  // curve outside it is a trend estimate).
  const curves = useMemo(() => {
    const rows = chartData?.ea ?? [];
    const maturityData = rows
      .map((p) => [p[0], p[2] ?? p[1]])
      .filter((p) => p[1] != null) as [number, number][];
    const strengthData = rows
      .map((p) => [p[0], p[1]])
      .filter((p) => p[1] != null) as [number, number][];
    const targets = chartData?.target ?? [];
    const markerOptions = { strengthUnit: unit, meta, range: calibratedRange, concreteStart, monitoringStart };
    return {
      maturityData,
      strengthData,
      maturityPoints: maturityData.length > 0
        ? achievementPoints(targets, maturityData, { chart: 'maturity', ...markerOptions })
        : [],
      strengthPoints: achievementPoints(targets, strengthData, { chart: 'strength', ...markerOptions }),
      span: testedTimeSpan(rows, calibratedRange),
    };
  }, [chartData, unit, meta, calibratedRange, concreteStart, monitoringStart]);

  // The caption under the strength chart: the tested range in words, and
  // where targets reached past the drawn curve are marked instead.
  const strengthCaption = useMemo(() => {
    const onStrengthCurve = new Set(curves.strengthPoints.flatMap((p) => p.custom.targetIds));
    const offCurveTargets = curves.maturityPoints.some((p) =>
      p.custom.targetIds.some((id) => !onStrengthCurve.has(id)),
    );
    return strengthRangeCaption(calibratedRange, meta, unit, { offCurveTargets });
  }, [curves, calibratedRange, meta, unit]);

  // Maturity and strength charts apply to maturity locations on maturity
  // pours only: humidity, site-conditions hardware and Temperature Only
  // locations have neither.
  const showMaturityCharts = showMaturityTab && locationMaturity;
  // ASTM C1074 7.3: places where the readings are further apart than allowed
  // (the list is capped, so prefer the total count).
  const intervalIssues = chartData?.reading_interval_issues ?? [];
  const intervalIssueCount = Math.max(
    chartData?.reading_interval_issue_count ?? 0,
    intervalIssues.length,
  );
  /** 60 -> "60 min", 62.5 -> "62.5 min". */
  const gapMinutes = (minutes: number): string => `${Number(minutes.toFixed(1))} min`;

  /** A target's name for its chart lines: "Initial Stressing (32.0 MPa)",
   *  "32.0 MPa" without a purpose, else "Target". The purpose is the chart
   *  target's label, else the (deprecated) progress bar's name when that is
   *  a purpose: older chart rows lack target_label. */
  const targetName = (targetId: string): string => {
    const chartTarget = (chartData?.target ?? []).find((t) => t.target_id === targetId);
    const progressName = (chartData?.progress_bar?.targets ?? []).find((t) => t.target_id === targetId)?.target_name;
    return targetDisplayName(
      chartTarget?.target_label?.trim() || targetPurpose(progressName),
      chartTarget?.target_strength,
      unit,
    );
  };

  /** Configured strength targets as horizontal plotlines. `value` picks the
   *  axis the line belongs on (target strength or its maturity equivalent).
   *  The label names the target by purpose and strength; on the maturity
   *  axis (`maturityAxisUnit` given) it adds where the line sits. Each line
   *  has an id (targetPlotLineId) and a label priority, pending above
   *  reached, for declutterTargetLabels. */
  const getTargetYPlotLines = (
    value: (t: ChartTarget) => number | undefined,
    maturityAxisUnit?: string,
  ): { plotLines: Highcharts.YAxisPlotLinesOptions[]; priorityById: Record<string, number> } => {
    const priorityById: Record<string, number> = {};
    const plotLines = (chartData?.target ?? []).flatMap((t): Highcharts.YAxisPlotLinesOptions[] => {
      const v = value(t);
      if (v == null || !(v > 0)) return [];
      const id = targetPlotLineId(t.target_id);
      priorityById[id] = targetLabelPriority(!t.achieved);
      const colour = t.achieved ? '#2e7d32' : '#9a6a00';
      const at = maturityAxisUnit ? ` at ${v.toFixed(1)} ${maturityAxisUnit}` : '';
      const flag = targetOutside(t) ? `, ${TARGET_OUTSIDE_RANGE_LABEL.toLowerCase()}` : '';
      return [{
        id,
        value: v,
        color: colour,
        width: 1.5,
        dashStyle: 'Dash',
        label: {
          text: `${targetName(t.target_id)}${at}${flag}`,
          align: 'right',
          x: -8,
          style: { color: colour, fontSize: '11px', fontWeight: '600' },
        },
        zIndex: 5,
      }];
    });
    return { plotLines, priorityById };
  };

  // Maturity Chart Configuration: the raw maturity index (TTF for Nurse
  // Saul, EA for Arrhenius) calculated from the location's temperature track.
  // Target lines stay in view (softMax) with their labels decluttered on each
  // redraw, and a marker sits where each target was reached.
  const getMaturityChartOptions = (): Highcharts.Options => {
    const { maturityData, maturityPoints } = curves;
    const { plotLines, priorityById } = getTargetYPlotLines((t) => t.target_ea, maturityUnit);

    return Highcharts.merge<Highcharts.Options>(getBaseTimeSeriesOptions(
      `${maturityLong} vs Elapsed Time`,
      { timezoneIana, height: STACK_CHART_HEIGHT },
    ), {
      chart: {
        events: {
          render(this: Highcharts.Chart) {
            declutterTargetLabels(this, priorityById);
          },
        },
      },
      yAxis: {
        title: { text: meta.axis_title },
        opposite: false,
        min: 0,
        softMax: maturityAxisSoftMax((chartData?.target ?? []).map((t) => t.target_ea)),
        plotLines,
      },
      legend: { enabled: false },
      tooltip: { valueDecimals: 1, valueSuffix: ` ${maturityUnit}` },
      series: maturityData.length > 0 ? [{
        name: maturityLong,
        type: 'areaspline',
        data: maturityData,
        threshold: null,
        fillColor: areaFill(MATURITY_COLOR),
        marker: { enabled: false, radius: 2 },
        color: MATURITY_COLOR,
      }, ...reachedMarkerSeries(maturityPoints)] : [{
        name: 'No data',
        type: 'spline',
        data: [],
      }],
    });
  };

  // Strength Chart Configuration: estimated strength from the pour's
  // calibration curve (only plotted when a calibration is assigned). The
  // tested range is a shaded band; outside the span of time the maturity was
  // inside it the curve is faded and dashed (the trend extension), and its
  // tooltip says so. Markers sit where targets were reached on the drawn
  // curve; the caption under the chart says the rest.
  const getStrengthChartOptions = (): Highcharts.Options => {
    const { strengthData, strengthPoints, span } = curves;
    const { plotLines, priorityById } = getTargetYPlotLines((t) => t.target_strength);
    let dataMax: number | null = null;
    strengthData.forEach(([, value]) => {
      if (dataMax == null || value > dataMax) dataMax = value;
    });

    return Highcharts.merge<Highcharts.Options>(getBaseTimeSeriesOptions(
      'Strength vs Elapsed Time',
      { timezoneIana, height: STACK_CHART_HEIGHT },
    ), {
      chart: {
        events: {
          render(this: Highcharts.Chart) {
            declutterTargetLabels(this, priorityById);
          },
        },
      },
      yAxis: {
        title: { text: `Strength (${unit})` },
        opposite: false,
        min: 0,
        softMax: strengthAxisSoftMax(
          calibratedRange,
          (chartData?.target ?? []).map((t) => t.target_strength),
          dataMax,
        ),
        plotBands: testedRangeBand(calibratedRange),
        plotLines,
      },
      legend: { enabled: false },
      tooltip: { valueDecimals: 1, valueSuffix: ` ${unit}` },
      series: strengthData.length > 0 ? [{
        name: 'Estimated Strength',
        type: 'areaspline',
        data: strengthData,
        threshold: null,
        fillColor: areaFill(STRENGTH_COLOR),
        marker: { enabled: false, radius: 2 },
        color: STRENGTH_COLOR,
        zoneAxis: 'x',
        zones: extrapolatedZones(span, STRENGTH_COLOR),
        tooltip: {
          pointFormatter: function (this: Highcharts.Point) {
            const line = `<span style="color:${this.color}">\u25CF</span> ${this.series.name}: `
              + `<b>${formatHalfUp(this.y ?? 0)} ${unit}</b><br/>`;
            return outsideTestedSpan(this.x, span) ? `${line}<i>${TREND_ESTIMATE_TOOLTIP}</i><br/>` : line;
          },
        },
      }, ...reachedMarkerSeries(strengthPoints)] : [{
        name: 'No data',
        type: 'spline',
        data: [],
      }],
    });
  };

  // Temperature Differential Chart Configuration
  const getDiffChartOptions = (): Highcharts.Options => {
    const diffSeries: Highcharts.SeriesOptionsType[] = [];

    if (chartData?.difftemp) {
      Object.entries(chartData.difftemp).forEach(([locationId, data]) => {
        diffSeries.push({
          name: `Diff to ${locationId}`,
          type: 'spline',
          data: data,
          marker: { enabled: false, radius: 2 }
        });
      });
    }
    
    // Add diff limit plotlines
    const plotLines: Highcharts.YAxisPlotLinesOptions[] = [];
    if (chartData?.diffLimit) {
      chartData.diffLimit.forEach((limit) => {
        plotLines.push({
          value: limit.value,
          color: 'red',
          width: 2,
          dashStyle: 'Dash',
          label: {
            text: `Limit ${limit.operator} ${limit.value}°C`,
            align: 'right',
            x: -8,
            style: { color: 'red', fontSize: '11px' }
          },
          zIndex: 5
        });
      });
    }
    
    return Highcharts.merge<Highcharts.Options>(getBaseTimeSeriesOptions('Temperature Differential vs Elapsed Time', { timezoneIana, height: STACK_CHART_HEIGHT }), {
      yAxis: {
        title: { text: 'Temperature Differential (°C)' },
        opposite: false,
        plotLines
      },
      legend: { enabled: diffSeries.length > 1 },
      tooltip: {
        // All locations in one tooltip so differentials can be compared at
        // the same point in time.
        shared: true,
        split: false,
        valueDecimals: 1,
        valueSuffix: '°C'
      },
      series: diffSeries.length > 0 ? diffSeries : [{
        name: 'No data',
        type: 'spline',
        data: []
      }]
    });
  };

  // Each chart's options are built again only when what they show changes
  // (a new response, the unit or naming, the reset permission), so other
  // re-renders (the gaps list, the rail) do not update the charts. The keys
  // list what each builder reads; the builders are plain functions.
  /* eslint-disable react-hooks/exhaustive-deps */
  const tempChartOptions = useMemo(
    () => getTempChartOptions(),
    [chartData, timezoneIana, isHumidity, isSiteHardware, pourLimits, canResetConcreteStart],
  );
  const evaChartOptions = useMemo(() => getEvaChartOptions(), [chartData, timezoneIana]);
  const diffChartOptions = useMemo(() => getDiffChartOptions(), [chartData, timezoneIana]);
  const maturityChartOptions = useMemo(
    () => getMaturityChartOptions(),
    [chartData, timezoneIana, meta, unit, curves],
  );
  const strengthChartOptions = useMemo(
    () => getStrengthChartOptions(),
    [chartData, timezoneIana, meta, unit, curves],
  );
  /* eslint-enable react-hooks/exhaustive-deps */

  // Freshness of the selected location: the bands, "Logging ended" once the
  // pour is completed or this location's logging finished (grey, never a red
  // "Offline"), or "Scheduled" before its planned start.
  const lastReading = chartData?.current?.last_reading;
  const startsAt = chartData?.location_details?.scheduled_start ?? locationStartsAt(selectedLocation);
  // Monitoring has ended: the pour is completed or this location's logging
  // finished. The Targets card then says "short when monitoring ended".
  const now = Date.now();
  const monitoringEnded =
    pourStatusEnded(chartData?.pour_status) ||
    pourStatusEnded(pourStatus) ||
    isLoggingEnded({ logging_ended: chartData?.location_details?.logging_finished }, null, now) ||
    isLoggingEnded(selectedLocation, null, now);
  const freshness = getOnlineStatus(lastReading, now, {
    ended: monitoringEnded,
    startsAt,
  });

  if (!locations.length) {
    return (
      <Alert severity="info">No locations available</Alert>
    );
  }

  return (
    <Box>
      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Main Layout: stacked graphs + collapsible right rail */}
      <Grid container spacing={2}>
        {/* MAIN: selector, KPI strip, targets, stacked graphs */}
        <Grid size={{ xs: 12, lg: railOpen ? 9 : 12 }}>
          {/* Header card: location selector + online status, then a compact
              left-aligned stat bar of the key metrics. */}
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            {/* Row 1: selector + rail toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 200, flexGrow: { xs: 1, sm: 0 } }}>
                <Select value={selectedLocationId} onChange={handleLocationChange}>
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name || `Location ${location.id.slice(-8)}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ flexGrow: 1 }} />
              {!railOpen && (
                <Button
                  size="small"
                  onClick={() => setRailOpen(true)}
                  startIcon={<ChevronLeftIcon />}
                  sx={{ textTransform: 'none', flexShrink: 0, color: 'primary.main' }}
                >
                  Event Time Line
                </Button>
              )}
            </Box>

            {/* Freshness banner — full-width, tinted by logger freshness. A
                logger without readings shows it only when waiting for its
                start or finished (the chart's last reading can be missing
                for a logger that did report). */}
            {!loading && chartData && (lastReading || freshness.level !== 'offline') && (() => {
              const tint = FRESHNESS_TINTS[freshness.level];
              const icon = freshness.level === 'scheduled'
                ? <ScheduleIcon sx={{ fontSize: 16 }} />
                : freshness.level === 'online' || freshness.level === 'ended'
                  ? <CheckCircleIcon sx={{ fontSize: 16 }} />
                  : <CloudOffIcon sx={{ fontSize: 16 }} />;
              const detail = freshness.level === 'scheduled'
                ? `Starts ${formatDateTimeWithZone(startsAt, timezoneIana)}`
                : lastReading
                  ? `Last read ${formatDateTimeWithZone(lastReading, timezoneIana)}`
                  : freshness.awaiting && startsAt
                    ? `Started ${formatDateTimeWithZone(startsAt, timezoneIana)}`
                    : freshness.level === 'offline'
                      ? 'No readings received'
                      : null;
              return (
                <Box
                  data-testid="freshness-banner"
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5,
                    width: { xs: '100%', md: 'fit-content' },
                    bgcolor: tint.bg, color: tint.fg, borderRadius: 1.5, px: 1.5, py: 0.75,
                    fontSize: '0.8rem', fontWeight: 500,
                  }}
                >
                  {icon}
                  {freshness.label}{detail ? ` · ${detail}` : ''}
                </Box>
              );
            })()}

            {/* Row 2: KPI stat bar — natural width, left-aligned, divider-separated */}
            {loading && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(auto-fill, minmax(150px, 180px))' }, gap: 1, mt: 1.5 }}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <Box key={i} sx={{ bgcolor: 'grey.50', borderRadius: 1.5, p: 1.25 }}>
                    <Skeleton variant="text" width="70%" height={16} />
                    <Skeleton variant="text" width="90%" height={26} />
                  </Box>
                ))}
              </Box>
            )}
            {!loading && chartData && (() => {
              const c = chartData.current;
              const currentValue = c?.temperature;
              // The calibrated-range rule: "24.3 MPa", "< 3.3 MPa" / "> 41.0
              // MPa" outside the tested range (with a tooltip), "NCS*" for a
              // curve without included samples. Older responses send only
              // strength / min_strength.
              const reading = resolveStrengthReading(c?.strength_reading, c?.strength, c?.min_strength);
              const strengthDisplay = formatStrengthReading(reading, unit, 'N/A');
              const strengthTip = strengthReadingTooltip(reading);
              // Without a usable curve there is no strength: the maturity
              // KPI leads instead.
              const showStrengthKpi = hasCurve ?? (reading ? !reading.no_curve : hasStrengthData);
              const trend = c?.temp_trend;
              const trendLabel = trend === 'up' ? 'Rising' : trend === 'down' ? 'Falling' : 'Stable';
              // Show only the metrics this pour's functions need — mirrors the
              // graph gating: temperature/trend/max always; strength only for
              // maturity pours; evaporation only when that function is on.
              const kpis: React.ReactNode[] = [
                <KpiCell
                  key="temp"
                  label={isHumidity ? 'Current Humidity' : 'Current Temperature'}
                  value={currentValue != null ? `${currentValue.toFixed(1)}${valueUnit}` : 'N/A'}
                />,
              ];
              if (showMaturityCharts) {
                // Pours with a usable curve lead with strength (maturity as
                // sub-line); without one there is no strength, so lead with
                // TTF/EA. Locations without maturity (humidity, site hardware,
                // Temperature Only) have no maturity/strength, so skip.
                kpis.push(
                  showStrengthKpi ? (
                    <KpiCell
                      key="strength"
                      label="Estimated Strength"
                      color="primary.main"
                      value={strengthDisplay}
                      tooltip={strengthTip ?? undefined}
                      sub={c?.ea != null ? formatMaturityWithIndex(c.ea, meta) : undefined}
                    />
                  ) : (
                    <KpiCell
                      key="maturity"
                      label={maturityLong}
                      color="primary.main"
                      value={formatMaturity(c?.ea, meta, 1, 'N/A')}
                    />
                  )
                );
              }
              kpis.push(
                <KpiCell
                  key="trend"
                  label={isHumidity ? 'Humidity Trend' : 'Temperature Trend'}
                  value={<>{getTempTrendIcon(trend)}<span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{trendLabel}</span></>}
                />
              );
              if (showEvaporationTab) {
                kpis.push(
                  <KpiCell
                    key="eva"
                    label="Evaporation Rate"
                    value={c?.evaporation != null ? `${c.evaporation.toFixed(2)} kg/m²/h` : 'N/A'}
                  />
                );
              }
              if (c?.max_temperature != null) {
                kpis.push(
                  <KpiCell
                    key="max"
                    label={isHumidity ? 'Max Humidity' : 'Max Temp'}
                    // A hot concrete peak is a warning; a humidity peak isn't.
                    color={isHumidity ? 'text.primary' : 'error.main'}
                    value={`${c.max_temperature.toFixed(1)}${valueUnit}`}
                    sub={c.max_temperature_time ? formatDateTimeWithZone(c.max_temperature_time, timezoneIana) : undefined}
                  />
                );
              }
              const ld = chartData.location_details;
              kpis.push(
                <KpiCell
                  key="signal"
                  label="Signal / Battery"
                  value={
                    <>
                      <SignalCellularAlt sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <span>{ld?.signal_strength || 'N/A'}</span>
                      <Box component="span" sx={{ mx: 0.25, color: 'text.disabled' }}>·</Box>
                      <Battery80 sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <span>{formatBatteryLevel(ld?.battery_level)}</span>
                    </>
                  }
                />
              );
              return (
                <Box
                  sx={{
                    display: 'grid',
                    // Mobile: two equal columns. Desktop: content-width boxes
                    // packed left so they don't stretch across the whole row.
                    gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(auto-fill, minmax(150px, 180px))' },
                    gap: 1,
                    mt: 1.5,
                  }}
                >
                  {kpis.map((k, i) => (
                    <React.Fragment key={i}>{k}</React.Fragment>
                  ))}
                </Box>
              );
            })()}

            {/* ASTM C1074 7.3: readings too far apart for this location. The
                maths is unchanged; the gaps are flagged. The compact warning
                expands to the listed gaps in the pour's timezone. */}
            {!loading && showMaturityCharts && intervalIssueCount > 0 && (
              <Alert
                severity="warning"
                data-testid="reading-interval-warning"
                sx={{ mt: 1.5, py: 0.25, '& .MuiAlert-message': { fontSize: '0.8rem', flex: 1, minWidth: 0 } }}
              >
                {READING_INTERVAL_WARNING} Gaps found: {intervalIssueCount}.
                {intervalIssues.length > 0 && (
                  <>
                    {' '}
                    <Button
                      size="small"
                      color="inherit"
                      aria-expanded={gapsOpen}
                      onClick={() => setGapsOpen((open) => !open)}
                      sx={{
                        p: 0,
                        minWidth: 0,
                        verticalAlign: 'baseline',
                        fontSize: 'inherit',
                        fontWeight: 600,
                        textTransform: 'none',
                        textDecoration: 'underline',
                      }}
                    >
                      {gapsOpen ? 'Hide the gaps' : 'Show the gaps'}
                    </Button>
                    <Collapse in={gapsOpen} unmountOnExit>
                      <Box sx={{ overflowX: 'auto', mt: 1 }}>
                        <Table
                          size="small"
                          aria-label="Reading gaps"
                          sx={{ '& th, & td': { fontSize: '0.75rem', py: 0.5, px: 1, borderColor: 'rgba(0,0,0,0.08)' } }}
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell>Start</TableCell>
                              <TableCell>End</TableCell>
                              <TableCell align="right">Length</TableCell>
                              <TableCell align="right">Limit</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {intervalIssues.map((gap, i) => (
                              <TableRow key={`${gap.start}-${i}`}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTimeWithZone(gap.start, timezoneIana)}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTimeWithZone(gap.end, timezoneIana)}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{gapMinutes(gap.minutes)}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{gapMinutes(gap.limit_minutes)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                      {intervalIssueCount > intervalIssues.length && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          Showing the first {intervalIssues.length} of {intervalIssueCount} gaps.
                        </Typography>
                      )}
                    </Collapse>
                  </>
                )}
              </Alert>
            )}
          </Paper>

          {/* Targets: each target's strength, the maturity it needs and its
              status (maturity concept: not for humidity, site hardware or
              Temperature Only locations). */}
          {!loading && showMaturityCharts && chartData && (chartData.target?.length ?? 0) > 0 && (
            <TargetsCard
              targets={chartData.target}
              meta={meta}
              strengthUnit={unit}
              calibratedRange={calibratedRange}
              currentMaturity={chartData.current?.ea}
              concreteStart={concreteStart}
              monitoringStart={monitoringStart}
              ended={monitoringEnded}
              timezoneIana={timezoneIana}
              calibrationCheckStatus={calibrationCheckStatus}
              gradeStrength={chartData.product_strength_grade}
              gradeOutsideRange={chartData.grade_outside_range}
            />
          )}

          {/* Function-relevant graphs, stacked (no tabs) */}
          {loading ? (
            Array.from({ length: 2 }).map((_, i) => (
              <Paper key={`gskel-${i}`} elevation={2} sx={{ p: 2, mb: 2 }}>
                <Skeleton variant="text" width={280} height={32} sx={{ mb: 1 }} />
                <Skeleton variant="rounded" height={STACK_CHART_HEIGHT} />
              </Paper>
            ))
          ) : (
            (() => {
              // Temperature is the prerequisite of every monitoring function,
              // so it always shows; the rest are gated on what the pour needs.
              // The temperature chart is rebuilt when the reset permission
              // changes (e.g. a refresh finds the pour locked): Highcharts'
              // update keeps series options the new ones leave out, so the
              // old pointer cursor and click handler would otherwise stay.
              const graphDefs: Array<{ label: string; render: () => React.ReactNode }> = [
                { label: isHumidity ? 'Relative Humidity vs. Elapsed Time' : 'Temperature vs. Elapsed Time', render: () => (<HighchartsReact key={canResetConcreteStart ? 'reset-on' : 'reset-off'} highcharts={Highcharts} constructorType={'stockChart'} options={tempChartOptions} />) },
              ];
              // Differential needs a second location to compare against — show
              // it whenever the chart actually has series to plot. Gate on
              // `difftemp` (the plotted data) rather than
              // `temp_differential_locations` (the sidebar table), because that
              // table is suppressed when there's no *live* current temp, which
              // would wrongly hide the chart for frozen/complete pours that
              // still have full historical differential data.
              if (!isHumidity && !isSiteHardware && Object.keys(chartData?.difftemp ?? {}).length > 0) {
                graphDefs.push({ label: 'Temperature Differential vs. Elapsed Time', render: () => (<HighchartsReact highcharts={Highcharts} constructorType={'stockChart'} options={diffChartOptions} />) });
              }
              // Per-location analysis for concrete maturity channels: the
              // calculated maturity index, then the strength curve when the
              // pour has a calibration (strength is null without one). The
              // pour-level Strength Charts section overlays every location;
              // this is the single-location view.
              if (showMaturityCharts) {
                graphDefs.push({ label: `${maturityLong} vs. Elapsed Time`, render: () => (<HighchartsReact highcharts={Highcharts} constructorType={'stockChart'} options={maturityChartOptions} />) });
                if (hasStrengthData) {
                  graphDefs.push({ label: 'Strength vs. Elapsed Time', render: () => (
                    <>
                      <HighchartsReact highcharts={Highcharts} constructorType={'stockChart'} options={strengthChartOptions} />
                      {strengthCaption && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          data-testid="strength-range-caption"
                          sx={{ mt: 1, lineHeight: 1.4 }}
                        >
                          {strengthCaption}
                        </Typography>
                      )}
                    </>
                  ) });
                }
              }
              if (showEvaporationTab) {
                graphDefs.push({ label: 'Evaporation vs. Elapsed Time', render: () => (<HighchartsReact highcharts={Highcharts} constructorType={'stockChart'} options={evaChartOptions} />) });
              }
              return graphDefs.map((g) => (
                <Paper key={g.label} elevation={2} sx={{ p: 2, mb: 2 }}>
                  {g.render()}
                </Paper>
              ));
            })()
          )}
        </Grid>

        {/* RIGHT RAIL — Event Timeline + logger card (Figma sidebar) */}
        {railOpen && (
          <Grid size={{ xs: 12, lg: 3 }} sx={{ alignSelf: 'flex-start' }}>
            {/* Rail sizes to its own content: with several graphs stacked on
                the left the row is very tall, and stretching the panel to
                match left a large empty green block under the logger card. */}
            <Paper elevation={1} sx={{ p: 2, bgcolor: 'rgba(121, 168, 42, 0.06)', display: 'flex', flexDirection: 'column' }}>
              {/* Header + collapse handle */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => setRailOpen(false)}
                  sx={{ p: 0.25, color: 'primary.main' }}
                  aria-label="Hide timeline panel"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
                <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                  Event Time Line
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.4 }}>
                {isAmbientView || isHumidity
                  ? 'Shows monitoring events for this location in time order.'
                  : 'Shows pour events, temperature limit breaches and strength targets for this location in time order.'}
              </Typography>

              {/* Timeline (header supplied above). Shown for every channel
                  type; humidity and site hardware get admin events only. */}
              {!loading && chartData && (
                <EventTimeline
                  hideHeader
                  chartData={chartData}
                  ambient={isAmbientView || isHumidity}
                  showStrength={showMaturityCharts}
                  maturityUnit={maturityUnit}
                  strengthUnit={unit}
                  timezoneIana={timezoneIana}
                />
              )}

              {/* Temperature Differential (mass-concrete pours; not humidity) */}
              {!loading && !isHumidity && !isSiteHardware && chartData?.temp_differential_locations && chartData.temp_differential_locations.length > 0 && (
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                    Temperature Differential
                  </Typography>
                  <Table size="small" sx={{ '& td, & th': { py: 0.5, fontSize: '0.8rem' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Location</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Current</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Max</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {chartData.temp_differential_locations.map((loc) => (
                        <TableRow key={loc.location_id}>
                          <TableCell>{loc.location_name}</TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              backgroundColor: loc.current_diff_color || 'transparent',
                              color: loc.current_diff_text_color || 'inherit',
                              fontWeight: 'bold'
                            }}
                          >
                            {loc.current_diff != null ? `${loc.current_diff.toFixed(1)}°C` : '-'}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              backgroundColor: loc.max_diff_color || 'transparent',
                              color: loc.max_diff_text_color || 'inherit',
                              fontWeight: 'bold'
                            }}
                          >
                            {loc.max_diff != null ? (
                              <Tooltip title={formatDateTimeWithZone(loc.max_diff_time, timezoneIana)} placement="top">
                                <span>{loc.max_diff.toFixed(1)}°C</span>
                              </Tooltip>
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {/* Logger card — footer of the rail */}
              {!loading && chartData?.location_details && (() => {
                const ld = chartData.location_details;
                const lastRead = lastReading;
                const online = lastRead ? freshness : null;
                const rows: Array<[string, string]> = [
                  ['Logger Number', ld.logger_number || 'N/A'],
                  ['Signal / Battery', `${ld.signal_strength || 'N/A'} / ${formatBatteryLevel(ld.battery_level)}`],
                  // Concrete start is a placement milestone — omit for site
                  // hardware and on ambient pours. Named as on the pour
                  // pages, with what it does.
                  ...(isAmbientView
                    ? []
                    : [['Concrete start (maturity counted from)', ld.ea_started ? formatDateTimeWithZone(ld.ea_started, timezoneIana) : 'N/A'] as [string, string]]),
                  ['Monitoring start time', ld.logging_started ? formatDateTimeWithZone(ld.logging_started, timezoneIana) : 'N/A'],
                ];
                return (
                  <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    {/* Device + last read, left-aligned so it reads as a quiet footer */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
                      <Box
                        component="img"
                        src="/logger.png"
                        alt="Concrete logger"
                        sx={{ height: 40, width: 'auto', objectFit: 'contain', flexShrink: 0 }}
                      />
                      {lastRead && online && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: online.color, flexShrink: 0 }} />
                          <Typography variant="caption" sx={{ color: online.color, fontSize: '0.72rem' }}>
                            Last Read {formatDateTimeWithZone(lastRead, timezoneIana)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {rows.map(([label, value]) => (
                        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary" fontSize="0.7rem">{label}</Typography>
                          <Typography variant="body2" fontSize="0.78rem" fontWeight={500} sx={{ textAlign: 'right' }}>{value}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })()}
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* EA Start Reset Confirmation Dialog */}
      <Dialog
        open={eaResetDialogOpen}
        onClose={handleEAResetCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change concrete start</DialogTitle>
        <DialogContent>
          <Typography>
            Change the concrete start for this location to{' '}
            <strong>
              {selectedEAStartTime
                ? formatDateTimeWithZone(selectedEAStartTime, timezoneIana)
                : ''}
            </strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Maturity is counted from the concrete start, so it is recalculated from this
            time using the recorded temperatures. Readings before it no longer count.
            Targets already met are marked at the time they were reached. No emails are sent.
          </Typography>
          {eaResetError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {eaResetError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEAResetCancel} disabled={eaResetLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleEAResetConfirm} 
            variant="contained" 
            color="primary"
            disabled={eaResetLoading}
            startIcon={eaResetLoading ? <CircularProgress size={20} /> : null}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
